// Chaque item de menu peut porter une clé `permission` (code de permission
// requis, ex. 'FACULTY_LIST') ou `permissions` (tableau, le user doit en
// avoir au moins une). Un item sans ces clés est visible par tout compte
// authentifié. Le filtrage effectif se fait dans SideBar.js via
// filterMenuByPermissions(), à partir des permissions du compte connecté.
// L'ordre ci-dessous suit le flux logique d'utilisation de l'application :
// 1. Tableau de bord (point d'entrée)
// 2. Structure académique - à configurer en premier, tout le reste en dépend
//    (écoles -> départements -> niveaux -> années -> semestres -> classes
//    -> groupes -> matières)
// 3. Utilisateurs - dépendent de la structure académique (rôles, étudiants,
//    enseignants, puis inscriptions qui nécessitent classes + années)
// 4. Évaluations - regroupe tout le circuit d'un examen, dans l'ordre où on
//    l'utilise :
//      a. Banque de questions (dépend des matières)
//      b. Cas cliniques / Valeurs de référence - ressources utilisées par
//         certaines questions (dossiers progressifs, calcul de dosage)
//      c. Examens - composition, circuit de validation (commission) et
//         publication ; regroupe aussi les sessions, salles et tablettes
//         nécessaires pour les faire passer
//      d. Résultats & passations - tentatives, résultats, incidents survenus
//         pendant l'examen, demandes de correction de note
// 5. Espace étudiant ("Mes examens")
// 6. Supervision (statistiques, audit, paramètres, notifications, import/export)
// 7. Système (pages techniques)
export const MenuList = [
    {
        title: 'Principal', titleKey: 'menu.section.main',
        classsChange: 'menu-title',
    },
    {
        title: 'Tableau de bord', titleKey: 'menu.dashboard.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-layout-dashboard"></i>,
        content: [
            {
                title: 'Tableau de bord', titleKey: 'menu.dashboard.admin',
                to: 'dashboard'
            },
            {
                title: 'Tableau de bord enseignants', titleKey: 'menu.dashboard.teacher',
                to: 'teacher-dashboard',
            },
        ],
    },
    {
        title: 'Structure académique', titleKey: 'menu.section.academicStructure',
        classsChange: 'menu-title',
    },
    {
        title: 'Écoles', titleKey: 'menu.faculties.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-building"></i>,
        content: [
            {
                title: 'Gestion des écoles', titleKey: 'menu.faculties.manage',
                to: 'faculties',
                permission: 'FACULTY_LIST',
            },
        ],
    },
    {
        title: 'Départements', titleKey: 'menu.departments.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-building-community"></i>,
        content: [
            {
                title: 'Gestion des départements', titleKey: 'menu.departments.manage',
                to: 'departments',
                permission: 'DEPARTMENT_LIST',
            },
        ],
    },
    {
        title: 'Niveaux', titleKey: 'menu.levels.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-stairs-up"></i>,
        content: [
            {
                title: 'Gestion des niveaux', titleKey: 'menu.levels.manage',
                to: 'levels',
                permission: 'LEVEL_LIST',
            },
        ],
    },
    {
        title: 'Années universitaires', titleKey: 'menu.academicYears.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-calendar-stats"></i>,
        content: [
            {
                title: 'Gestion des années universitaires', titleKey: 'menu.academicYears.manage',
                to: 'academic-years',
                permission: 'ACADEMIC_YEAR_LIST',
            },
        ],
    },
    {
        title: 'Semestres', titleKey: 'menu.semesters.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-calendar-time"></i>,
        content: [
            {
                title: 'Gestion des semestres', titleKey: 'menu.semesters.manage',
                to: 'semesters',
                permission: 'SEMESTER_LIST',
            },
        ],
    },
    {
        title: 'Classes', titleKey: 'menu.classes.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-chalkboard"></i>,
        content: [
            {
                title: 'Gestion des classes', titleKey: 'menu.classes.manage',
                to: 'classes',
                permission: 'CLASS_LIST',
            },
        ],
    },
    {
        title: 'Groupes', titleKey: 'menu.studentGroups.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-users-group"></i>,
        content: [
            {
                title: 'Gestion des groupes', titleKey: 'menu.studentGroups.manage',
                to: 'student-groups',
                permission: 'STUDENT_GROUP_LIST',
            },
        ],
    },
    {
        title: 'Matières', titleKey: 'menu.subjects.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-book-2"></i>,
        content: [
            {
                title: 'Gestion des matières', titleKey: 'menu.subjects.manage',
                to: 'subjects',
                permission: 'SUBJECT_LIST',
            },
        ],
    },
    {
        title: 'Utilisateurs', titleKey: 'menu.section.users',
        classsChange: 'menu-title',
    },
    {
        title: 'Utilisateurs & privilèges', titleKey: 'menu.usersPrivileges.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-shield-lock"></i>,
        content: [
            {
                title: 'Utilisateurs', titleKey: 'menu.usersPrivileges.users',
                to: 'users',
                permission: 'USER_LIST',
            },
            {
                title: 'Rôles', titleKey: 'menu.usersPrivileges.roles',
                to: 'roles',
                // Accès réservé au SUPER_ADMIN côté route (AdminRoute) et
                // backend (authorize('SUPER_ADMIN'), pas permit()) : une
                // permission ROLE_LIST éventuellement accordée à un autre
                // rôle ne suffit pas, donc on ne la teste pas ici. `content`
                // vide + `superAdminOnly` => l'item est retiré du menu par
                // filterMenuByPermissions() pour tout compte non SUPER_ADMIN.
                superAdminOnly: true,
            },
            {
                title: 'Permissions', titleKey: 'menu.usersPrivileges.permissions',
                to: 'permissions',
                // Idem : réservé au SUPER_ADMIN, voir commentaire ci-dessus.
                superAdminOnly: true,
            },
        ],
    },
    {
        title: 'Étudiants', titleKey: 'menu.students.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-school"></i>,
        content: [
            {
                title: 'Liste des étudiants', titleKey: 'menu.students.list',
                to: 'student',
                permission: 'STUDENT_PROFILE_LIST',
            },
        ],
    },
    {
        title: 'Enseignants', titleKey: 'menu.teachers.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-users"></i>,
        content: [
            {
                title: 'Liste des enseignants', titleKey: 'menu.teachers.list',
                to: 'teacher',
                permission: 'TEACHER_PROFILE_LIST',
            },
        ],
    },
    {
        title: 'Personnel administratif', titleKey: 'menu.staffProfiles',
        classsChange: '',
        iconStyle: <i className="ti ti-briefcase"></i>,
        to: 'staff-profiles',
        permission: 'STAFF_PROFILE_LIST',
    },
    {
        title: 'Inscriptions', titleKey: 'menu.enrollments.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-user-check"></i>,
        content: [
            {
                title: 'Gestion des inscriptions', titleKey: 'menu.enrollments.manage',
                to: 'enrollments',
                permission: 'ENROLLMENT_LIST',
            },
        ],
    },
    {
        title: 'Évaluations', titleKey: 'menu.section.evaluations',
        classsChange: 'menu-title',
    },
    {
        title: 'Banque de questions', titleKey: 'menu.questionBank.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-help-circle"></i>,
        content: [
            { title: 'Types', titleKey: 'menu.questionBank.types', to: 'question-types', permission: 'QUESTION_TYPE_LIST' },
            { title: 'Difficultés', titleKey: 'menu.questionBank.difficulties', to: 'question-difficulties', permission: 'QUESTION_DIFFICULTY_LIST' },
            { title: 'Catégories', titleKey: 'menu.questionBank.categories', to: 'question-categories', permission: 'QUESTION_CATEGORY_LIST' },
            { title: 'Questions', titleKey: 'menu.questionBank.questions', to: 'questions', permission: 'QUESTION_LIST' },
        ],
    },
    {
        title: 'Cas cliniques', titleKey: 'menu.clinicalCases',
        classsChange: '',
        iconStyle: <i className="ti ti-stethoscope"></i>,
        to: 'clinical-cases',
        permission: 'CLINICAL_CASE_LIST',
    },
    {
        title: 'Valeurs de référence', titleKey: 'menu.referenceValues',
        classsChange: '',
        iconStyle: <i className="ti ti-flask"></i>,
        to: 'reference-values',
        permission: 'REFERENCE_VALUE_LIST',
    },
    {
        title: 'Examens', titleKey: 'menu.exams.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-clipboard-text"></i>,
        content: [
            // Circuit : brouillon -> validé (commission des examens) -> publié -> archivé
            { title: 'Examens', titleKey: 'menu.exams.exams', to: 'exams', permission: 'EXAM_LIST' },
            { title: "Sessions d'examen", titleKey: 'menu.exams.sessions', to: 'exam-sessions', permission: 'EXAM_SESSION_LIST' },
            { title: "Salles d'examen", titleKey: 'menu.exams.rooms', to: 'rooms', permission: 'ROOM_LIST' },
            { title: 'Tablettes', titleKey: 'menu.exams.tablets', to: 'tablets', permission: 'TABLET_LIST' },
        ],
    },
    {
        title: 'Résultats & passations', titleKey: 'menu.results.title',
        classsChange: 'mm-collapse',
        iconStyle: <i className="ti ti-report-analytics"></i>,
        content: [
            { title: 'Tentatives', titleKey: 'menu.results.attempts', to: 'attempts', permission: 'STUDENT_ATTEMPT_LIST' },
            { title: 'Résultats', titleKey: 'menu.results.results', to: 'results', permission: 'EXAM_RESULT_LIST' },
            { title: "Incidents d'examen", titleKey: 'menu.results.incidents', to: 'exam-incidents', permission: 'EXAM_INCIDENT_LIST' },
            { title: 'Corrections de note', titleKey: 'menu.results.gradeCorrections', to: 'grade-correction-requests', permission: 'GRADE_CORRECTION_REQUEST_LIST' },
        ],
    },
    {
        title: 'Espace étudiant', titleKey: 'menu.section.studentSpace',
        classsChange: 'menu-title',
    },
    {
        title: 'Mes examens', titleKey: 'menu.myExams',
        classsChange: '',
        iconStyle: <i className="ti ti-school"></i>,
        to: 'my-exams',
        permission: 'STUDENT_ATTEMPT_START',
    },
    {
        title: 'Supervision', titleKey: 'menu.section.supervision',
        classsChange: 'menu-title',
    },
    {
        title: 'Statistiques', titleKey: 'menu.statistics',
        classsChange: '',
        iconStyle: <i className="ti ti-chart-bar"></i>,
        to: 'statistics',
        permission: 'STATISTIC_LIST',
    },
    {
        title: "Journal d'audit", titleKey: 'menu.auditLogs',
        classsChange: '',
        iconStyle: <i className="ti ti-history"></i>,
        to: 'audit-logs',
        permission: 'AUDIT_LOG_LIST',
    },
    {
        title: 'Paramètres système', titleKey: 'menu.settings',
        classsChange: '',
        iconStyle: <i className="ti ti-settings"></i>,
        to: 'settings',
        permission: 'SETTING_LIST',
    },
    {
        title: 'Notifications', titleKey: 'menu.notifications',
        classsChange: '',
        iconStyle: <i className="ti ti-bell"></i>,
        to: 'notifications',
        // Chaque compte gère ses propres notifications : aucune permission requise.
    },
    {
        title: 'Import / Export', titleKey: 'menu.importExport',
        classsChange: '',
        iconStyle: <i className="ti ti-file-spreadsheet"></i>,
        to: 'import-export',
        // Pas de garde unique : la page ne montre que les entités pour
        // lesquelles le compte a MODULE_IMPORT ou MODULE_EXPORT.
    },
    // {
    //     title: 'Système', titleKey: 'menu.section.system',
    //     classsChange: 'menu-title',
    // },
    // {
    //     title: 'Pages', titleKey: 'menu.pages.title',
    //     classsChange: 'mm-collapse',
    //     iconStyle: <i className="ti ti-file-text"></i>,
    //     content: [
    //         {
    //             title: 'Erreurs', titleKey: 'menu.pages.errors.title',
    //             hasMenu: true,
    //             content: [
    //                 {
    //                     title: 'Erreur 400', titleKey: 'menu.pages.errors.400',
    //                     to: 'page-error-400',
    //                 },
    //                 {
    //                     title: 'Erreur 403', titleKey: 'menu.pages.errors.403',
    //                     to: 'page-error-403',
    //                 },
    //                 {
    //                     title: 'Erreur 404', titleKey: 'menu.pages.errors.404',
    //                     to: 'page-error-404',
    //                 },
    //                 {
    //                     title: 'Erreur 500', titleKey: 'menu.pages.errors.500',
    //                     to: 'page-error-500',
    //                 },
    //                 {
    //                     title: 'Erreur 503', titleKey: 'menu.pages.errors.503',
    //                     to: 'page-error-503',
    //                 },
    //             ],
    //         },
    //         {
    //             title: 'Écran de verrouillage', titleKey: 'menu.pages.lockScreen',
    //             to: 'page-lock-screen',
    //         },
    //     ]
    // },
]

/**
 * Filtre le MenuList selon les permissions du compte connecté.
 *
 * Règles :
 * - Un item avec `permission` n'est gardé que si `userPermissions` la contient.
 * - Un item avec `permissions` (tableau) n'est gardé que si `userPermissions`
 *   contient au moins l'une d'entre elles.
 * - Un item sans ces clés est toujours gardé (dashboard, notifications, pages
 *   système...).
 * - Un item conteneur (`content`) est gardé seulement si au moins un de ses
 *   enfants reste visible après filtrage (récursif, pour les sous-menus
 *   imbriqués comme "Pages > Erreurs").
 * - Un séparateur (`classsChange: 'menu-title'`) n'est gardé que si la
 *   section qui le suit contient au moins un item visible.
 */
export function hasRequiredPermission(
    item,
    userPermissions = [],
    userRoles = []
) {
    // Le SUPER_ADMIN voit tout
    if (userRoles.includes('SUPER_ADMIN')) {
        return true;
    }

    // Certains items (gestion des rôles/permissions) sont réservés au
    // SUPER_ADMIN quelles que soient les permissions accordées : reflète
    // AdminRoute côté frontend et authorize('SUPER_ADMIN') côté backend.
    if (item.superAdminOnly) {
        return false;
    }

    if (!item.permission && !item.permissions) {
        return true;
    }

    if (item.permission) {
        return userPermissions.includes(item.permission);
    }

    return item.permissions.some((p) => userPermissions.includes(p));
}

function filterItems(items, userPermissions, userRoles) {
    return items
        .map((item) => {
            if (item.content && item.content.length > 0) {
                const filteredContent = filterItems(
                    item.content,
                    userPermissions,
                    userRoles
                );

                if (filteredContent.length === 0) {
                    return null;
                }

                return {
                    ...item,
                    content: filteredContent,
                };
            }

            return hasRequiredPermission(
                item,
                userPermissions,
                userRoles
            )
                ? item
                : null;
        })
        .filter(Boolean);
}

export function filterMenuByPermissions(
    menuList,
    userPermissions = [],
    userRoles = []
) {

    // Le SUPER_ADMIN voit tout le menu
    if (userRoles.includes('SUPER_ADMIN')) {
        return menuList;
    }

    const filtered = [];

    for (let i = 0; i < menuList.length; i += 1) {

        const item = menuList[i];

        if (item.classsChange === 'menu-title') {

            let hasVisibleFollower = false;

            for (
                let j = i + 1;
                j < menuList.length &&
                menuList[j].classsChange !== 'menu-title';
                j += 1
            ) {

                const sibling = menuList[j];

                const visible =
                    sibling.content && sibling.content.length > 0
                        ? filterItems(
                            sibling.content,
                            userPermissions,
                            userRoles
                        ).length > 0
                        : hasRequiredPermission(
                            sibling,
                            userPermissions,
                            userRoles
                        );

                if (visible) {
                    hasVisibleFollower = true;
                    break;
                }
            }

            if (hasVisibleFollower) {
                filtered.push(item);
            }

            continue;
        }

        if (item.content && item.content.length > 0) {

            const filteredContent = filterItems(
                item.content,
                userPermissions,
                userRoles
            );

            if (filteredContent.length > 0) {
                filtered.push({
                    ...item,
                    content: filteredContent,
                });
            }

            continue;
        }

        if (
            hasRequiredPermission(
                item,
                userPermissions,
                userRoles
            )
        ) {
            filtered.push(item);
        }
    }

    return filtered;
}